/**
 * Pure helper functions for UsersTable.
 * No React/hook dependencies – fully unit-testable.
 */
import type { ReactNode } from 'react';
import { createElement } from 'react';
import type { TranslateFn } from '../../../../../../types/i18n';
import type { TableRow } from '../types';

/** Semantic color matching `StatusIndicator` from @insio/ui-toolkit */
type StatusColor = 'success' | 'warning' | 'error' | 'neutral';

export interface StatusConfig {
  /** Semantic color for `<StatusIndicator color={…}>` */
  color: StatusColor;
  label: string;
}

/**
 * Returns the visual status config for a table row.
 * Accepts pre-resolved translated labels to stay pure.
 */
export function getStatusConfig(
  row: TableRow,
  labels: { active: string; deactivated: string; pending: string; fallback: (s: string) => string }
): StatusConfig {
  if (row.type === 'invitation') {
    return { color: 'warning', label: labels.pending };
  }

  switch (row.data.status) {
    case 'ACTIVE':
      return { color: 'success', label: labels.active };
    case 'DEACTIVATED':
      return { color: 'error', label: labels.deactivated };
    default:
      return { color: 'neutral', label: labels.fallback(row.data.status) };
  }
}

export function getAvatarName(row: TableRow): string {
  if (row.type === 'invitation') return row.data.email;
  return row.data.displayName || row.data.email;
}

export function getRowKey(row: TableRow): string {
  return row.type === 'user' ? `user-${row.data.id}` : `inv-${row.data.id}`;
}

export function getRowClassName(row: TableRow): string {
  const base =
    'flex flex-wrap items-center gap-x-md gap-y-sm px-md py-md border-b border-border-base last:border-b-0 first:rounded-t-lg last:rounded-b-lg transition md:flex-nowrap';
  const deactivated = row.type === 'user' && row.data.status === 'DEACTIVATED';
  return `${base} ${deactivated ? 'bg-background-disabled opacity-60' : 'hover:bg-background-muted'}`;
}

/**
 * Renders a translation string with a bold interpolated value.
 *
 * Splits the i18n message by the first `{{key}}` placeholder and wraps
 * the corresponding value in `<b>`. Falls back to plain interpolation
 * when no placeholder is found.
 *
 * Pure function – receives the `t` function as an argument so it can
 * live outside React components and be unit-tested.
 */
export function boldInterpolation(
  t: TranslateFn,
  key: string,
  params: Record<string, string>
): ReactNode {
  const raw = t(key, { interpolation: { skipOnVariables: true } }) ?? '';
  const paramKey = Object.keys(params)[0];
  const placeholder = `{{${paramKey}}}`;
  const parts = raw.split(placeholder);

  if (parts.length < 2) {
    // No placeholder found – return plain interpolated text
    return t(key, params);
  }

  return [parts[0], createElement('b', { key: paramKey }, params[paramKey]), ...parts.slice(1)];
}
